import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { DEFAULT_STRIPE_STATE_MODEL, StripeStateModel } from './stripe.model';
import {
  AiwynPaymentMethod,
  BillingDetails,
  ContactApiService,
  PaymentMethodApiService,
  PaymentMethodType,
  SetupIntentsByBusinessEntity,
  StripeApiService,
} from '@aiwyn/api';
import { getLoadedResults, Loadable, LoadableHelper, LoadableStatus, ResetState, setLoadableInState } from '@aiwyn/shared';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import {
  DeletePaymentMethod,
  GetSavedPaymentMethods,
  GetSetupIntentClientSecret,
  GetSetupIntentClientSecretByBusinessEntity,
  GetStripeUserDetails,
  RemoveNewBillingDetails,
  RemoveTemporaryPaymentMethod,
  SavePaymentMethod,
  SetDefaultPaymentMethod,
  SetNewBillingDetails,
  SetProvidedBillingEmail,
  SetNewPaymentMethodType,
  SetPaymentMethodMode,
  SetSelectedBusinessEntityId,
  SetSelectedSavedPaymentMethodId,
  SetSuccessfulSetupIntentId,
  SetTemporaryPaymentMethod,
  UpdatePaymentMethodBillingDetails,
} from './stripe.actions';
import { AbstractPaymentMethod, PaymentMethodMode, StripePaymentMethod } from '../../models';
import { AdminUser, AuthService, ContactUser, GuestUser } from '@aiwyn/auth';
import { ConfigState } from '@aiwyn/config';
import { CommonSettingsState } from '@aiwyn/common/settings';

export const STRIPE_STATE_SLICE_NAME = 'stripe';

@Injectable({
  providedIn: 'root',
})
@State<StripeStateModel>({
  name: STRIPE_STATE_SLICE_NAME,
  defaults: DEFAULT_STRIPE_STATE_MODEL,
})
export class StripeState {
  @Selector()
  static getUserIdLoadable(state: StripeStateModel): Loadable<string> {
    return state?.userId;
  }

  @Selector([StripeState.getUserIdLoadable])
  static getUserIdStatus(userIdLoadable: Loadable<string>): LoadableStatus {
    return userIdLoadable?.status;
  }

  @Selector([StripeState.getUserIdLoadable])
  static getUserId(userIdLoadable: Loadable<string>): string {
    return userIdLoadable?.data;
  }

  @Selector()
  static getDefaultPaymentMethodIdLoadable(state: StripeStateModel): Loadable<string> {
    return state?.defaultPaymentMethodId;
  }

  @Selector([StripeState.getDefaultPaymentMethodIdLoadable])
  static getDefaultPaymentMethodIdStatus(defaultPaymentMethodIdLoadable: Loadable<string>): LoadableStatus {
    return defaultPaymentMethodIdLoadable?.status;
  }

  @Selector([StripeState.getDefaultPaymentMethodIdLoadable])
  static getDefaultPaymentMethodId(defaultPaymentMethodIdLoadable: Loadable<string>): string {
    return defaultPaymentMethodIdLoadable?.data;
  }

  @Selector()
  static getSelectedBusinessEntityId(state: StripeStateModel): number {
    return state?.selectedBusinessEntityId;
  }

  @Selector()
  static getRawSetupIntentClientSecretLoadable(state: StripeStateModel): Loadable<string> {
    return state?.setupIntentClientSecret;
  }

  @Selector()
  static getSetupIntentClientSecretByBusinessEntityLoadable(state: StripeStateModel): Loadable<SetupIntentsByBusinessEntity> {
    return state?.setupIntentClientSecretByBusinessEntity;
  }

  @Selector([
    ConfigState.getSetupIntentByBusinessEntity,
    StripeState.getRawSetupIntentClientSecretLoadable,
    StripeState.getSetupIntentClientSecretByBusinessEntityLoadable,
    StripeState.getSelectedBusinessEntityId,
  ])
  static getSetupIntentClientSecretLoadable(
    setupIntentByBusinessEntity: boolean,
    rawLoadable: Loadable<string>,
    loadable: Loadable<SetupIntentsByBusinessEntity>,
    selectedBusinessEntityId: number,
  ): Loadable<string> {
    if (!setupIntentByBusinessEntity) {
      return rawLoadable;
    }

    if (!loadable?.data) {
      return loadable as unknown as Loadable<string>;
    }

    return {
      ...loadable,
      data: selectedBusinessEntityId
        ? loadable.data.clientSecrets?.find(secret => secret.businessEntityId === selectedBusinessEntityId)?.clientSecret ?? loadable.data.default
        : loadable.data.default,
    };
  }

  @Selector([StripeState.getSetupIntentClientSecretLoadable])
  static getSetupIntentClientSecretStatus(setupIntentClientSecretLoadable: Loadable<string>): LoadableStatus {
    return setupIntentClientSecretLoadable?.status;
  }

  @Selector([StripeState.getSetupIntentClientSecretByBusinessEntityLoadable])
  static getSetupIntentClientSecretByBusinessEntityStatus(setupIntentClientSecretLoadable: Loadable<SetupIntentsByBusinessEntity>): LoadableStatus {
    return setupIntentClientSecretLoadable?.status;
  }

  @Selector([StripeState.getSetupIntentClientSecretLoadable])
  static getSetupIntentClientSecret(setupIntentClientSecretLoadable: Loadable<string>): string {
    return setupIntentClientSecretLoadable?.data;
  }

  @Selector()
  static getSuccessfulSetupIntentIds(state: StripeStateModel): Record<string, string> {
    return state?.successfulSetupIntentIds ?? {};
  }

  static getSuccessfulSetupIntentId(paymentMethodId: string): (successfulSetupIntentIds: Record<string, string>) => string {
    return createSelector(
      [StripeState.getSuccessfulSetupIntentIds],
      successfulSetupIntentIds => successfulSetupIntentIds[paymentMethodId],
    );
  }

  @Selector()
  static getRawSavedPaymentMethodsLoadable(state: StripeStateModel): Loadable<AiwynPaymentMethod[]> {
    return state?.savedPaymentMethods;
  }

  @Selector([
    StripeState.getRawSavedPaymentMethodsLoadable,
    StripeState.getDefaultPaymentMethodIdLoadable,
  ])
  static getSavedPaymentMethodsLoadable(
    rawSavedPaymentMethodsLoadable: Loadable<AiwynPaymentMethod[]>,
    defaultPaymentMethodIdLoadable: Loadable<string>,
  ): Loadable<StripePaymentMethod[]> {
    return {
      status: LoadableHelper.coalesceStatus([rawSavedPaymentMethodsLoadable?.status, defaultPaymentMethodIdLoadable?.status]),
      data: rawSavedPaymentMethodsLoadable?.data
        ?.map(paymentMethod => StripePaymentMethod.create(paymentMethod, defaultPaymentMethodIdLoadable?.data)),
    };
  }

  @Selector([StripeState.getRawSavedPaymentMethodsLoadable])
  static getSavedPaymentMethodsStatus(rawSavedPaymentMethodsLoadable: Loadable<AiwynPaymentMethod[]>): LoadableStatus {
    return rawSavedPaymentMethodsLoadable?.status;
  }

  @Selector([StripeState.getSavedPaymentMethodsLoadable])
  static getSavedPaymentMethods(savedPaymentMethodsLoadable: Loadable<StripePaymentMethod[]>): StripePaymentMethod[] {
    return savedPaymentMethodsLoadable?.data;
  }

  @Selector()
  static getRawTemporaryPaymentMethod(state: StripeStateModel): AiwynPaymentMethod {
    return state?.temporaryPaymentMethod;
  }

  @Selector([StripeState.getRawTemporaryPaymentMethod, StripeState.getDefaultPaymentMethodId])
  static getTemporaryPaymentMethod(temporaryPaymentMethod: AiwynPaymentMethod, defaultPaymentMethodId: string): AbstractPaymentMethod {
    return AbstractPaymentMethod.create(temporaryPaymentMethod, defaultPaymentMethodId);
  }

  @Selector()
  static getNewPaymentMethodType(state: StripeStateModel): PaymentMethodType {
    return state?.newPaymentMethodType;
  }

  @Selector()
  static getPaymentMethodMode(state: StripeStateModel): PaymentMethodMode {
    return state?.paymentMethodMode;
  }

  @Selector()
  static getSelectedSavedPaymentMethodId(state: StripeStateModel): string {
    return state?.selectedSavedPaymentMethodId;
  }

  @Selector([StripeState.getPaymentMethodMode, StripeState.getSelectedSavedPaymentMethodId, StripeState.getTemporaryPaymentMethod])
  static getSelectedPaymentMethodId(
    paymentMethodMode: PaymentMethodMode,
    selectedSavedPaymentMethodId: string,
    temporaryPaymentMethod: AbstractPaymentMethod,
  ): string {
    if (paymentMethodMode === PaymentMethodMode.NEW || paymentMethodMode === PaymentMethodMode.SET_UP_LATER) {
      return undefined;
    }

    return paymentMethodMode === PaymentMethodMode.TEMPORARY ? temporaryPaymentMethod?.id : selectedSavedPaymentMethodId;
  }

  @Selector()
  static getRawNewBillingDetails(state: StripeStateModel): BillingDetails {
    return state?.newBillingDetails;
  }

  @Selector()
  static getProvidedBillingEmail(state: StripeStateModel): string | undefined {
    return state?.providedBillingEmail;
  }

  constructor(
    private readonly stripeApiService: StripeApiService,
    private readonly paymentMethodApiService: PaymentMethodApiService,
    private readonly contactApiService: ContactApiService,
    private readonly authService: AuthService,
    private readonly store: Store,
  ) { }

  @Action([GetStripeUserDetails])
  getStripeUserDetails(
    { patchState }: StateContext<StripeStateModel>,
  ): Observable<{ userId: string; defaultPaymentMethodId: string; }> | void {
    if (
      !LoadableHelper.shouldRequest(this.store.selectSnapshot(StripeState.getUserIdStatus)) &&
      !LoadableHelper.shouldRequest(this.store.selectSnapshot(StripeState.getDefaultPaymentMethodIdStatus))
    ) {
      return;
    }

    patchState({
      userId: { status: LoadableStatus.LOADING },
      defaultPaymentMethodId: { status: LoadableStatus.LOADING },
    });

    return this.authService.getUser().pipe(
      switchMap(user => {
        if (user instanceof GuestUser) {
          return of({
            userId: undefined,
            defaultPaymentMethodId: undefined,
          });
        }

        if (user instanceof AdminUser) {
          return this.stripeApiService.getClientStripeData().pipe(
            map(clientStripeDataPayload => ({
              userId: clientStripeDataPayload.firmStripeCustomerId,
              defaultPaymentMethodId: clientStripeDataPayload?.firmStripeCustomerDefaultPaymentMethodId,
            })),
          );
        }

        return of({
          userId: user.getStripeUserId(),
          defaultPaymentMethodId: user.getStripeDefaultPaymentMethodId(),
        });
      }),
      tap(
        ({ userId, defaultPaymentMethodId }) => patchState({
          userId: { status: LoadableStatus.LOADED, data: userId },
          defaultPaymentMethodId: { status: LoadableStatus.LOADED, data: defaultPaymentMethodId },
        }),
        _ => patchState({
          userId: { status: LoadableStatus.ERROR, data: undefined },
          defaultPaymentMethodId: { status: LoadableStatus.ERROR, data: undefined },
        }),
      ),
      take(1),
    );
  }

  @Action([GetSetupIntentClientSecret])
  getSetupIntentClientSecret(
    { patchState }: StateContext<StripeStateModel>,
    { forceRefresh }: GetSetupIntentClientSecret,
  ): Observable<string> | void {
    const setupIntentClientSecretStatus = this.store.selectSnapshot(StripeState.getSetupIntentClientSecretStatus);
    if (!(LoadableHelper.shouldRequest(setupIntentClientSecretStatus) || forceRefresh)) {
      return;
    }

    patchState({ setupIntentClientSecret: { status: LoadableStatus.LOADING } });

    return this.stripeApiService.getSetupIntentClientSecret().pipe(
      setLoadableInState(patchState, 'setupIntentClientSecret'),
      take(1),
    );
  }

  @Action([SetSelectedBusinessEntityId])
  setSelectedBusinessEntityId(
    { patchState }: StateContext<StripeStateModel>,
    { businessEntityId }: SetSelectedBusinessEntityId,
  ): void {
    patchState({ selectedBusinessEntityId: businessEntityId });
  }

  @Action([GetSetupIntentClientSecretByBusinessEntity])
  getSetupIntentClientSecretByBusinessEntity(
    { patchState }: StateContext<StripeStateModel>,
    { forceRefresh }: GetSetupIntentClientSecret,
  ): Observable<SetupIntentsByBusinessEntity> {
    const setupIntentClientSecretStatus = this.store.selectSnapshot(StripeState.getSetupIntentClientSecretByBusinessEntityStatus);
    if (!(LoadableHelper.shouldRequest(setupIntentClientSecretStatus) || forceRefresh)) {
      return getLoadedResults(this.store.select(StripeState.getSetupIntentClientSecretByBusinessEntityLoadable)).pipe(take(1));
    }

    patchState({ setupIntentClientSecretByBusinessEntity: { status: LoadableStatus.LOADING } });

    return this.stripeApiService.getSetupIntentClientSecretByBusinessEntity().pipe(
      setLoadableInState(patchState, 'setupIntentClientSecretByBusinessEntity'),
    );
  }

  @Action([SetSuccessfulSetupIntentId])
  setSuccessfulSetupIntentId(
    { patchState }: StateContext<StripeStateModel>,
    { paymentMethodId, setupIntentId }: SetSuccessfulSetupIntentId,
  ): void {
    const successfulSetupIntentIds = this.store.selectSnapshot(StripeState.getSuccessfulSetupIntentIds);
    patchState({ successfulSetupIntentIds: { ...successfulSetupIntentIds, [paymentMethodId]: setupIntentId } });
  }

  @Action([GetSavedPaymentMethods])
  getSavedPaymentMethods({ patchState }: StateContext<StripeStateModel>): Observable<AiwynPaymentMethod[]> {
    patchState({
      savedPaymentMethods: {
        ...this.store.selectSnapshot(StripeState.getRawSavedPaymentMethodsLoadable),
        status: LoadableStatus.LOADING,
      },
    });

    return combineLatest([
      getLoadedResults(this.store.select(StripeState.getUserIdLoadable)),
      getLoadedResults(this.store.select(CommonSettingsState.getUsStatesLoadable)),
    ]).pipe(
      switchMap(([stripeUserId, usStates]) =>
        stripeUserId != undefined
          ? this.paymentMethodApiService.getSavedPaymentMethodsV5(stripeUserId, usStates)
          : of([]),
      ),
      setLoadableInState(patchState, 'savedPaymentMethods'),
      take(1),
    );
  }

  @Action([SetSelectedSavedPaymentMethodId])
  setSelectedPaymentMethodId(
    { patchState }: StateContext<StripeStateModel>,
    { selectedPaymentMethodId }: SetSelectedSavedPaymentMethodId,
  ): void {
    patchState({ selectedSavedPaymentMethodId: selectedPaymentMethodId });
  }

  @Action([SetTemporaryPaymentMethod])
  setTemporaryPaymentMethod(
    { patchState }: StateContext<StripeStateModel>,
    { temporaryPaymentMethod }: SetTemporaryPaymentMethod,
  ): void {
    patchState({ temporaryPaymentMethod });
  }

  @Action([RemoveTemporaryPaymentMethod])
  removeTemporaryPaymentMethod({ patchState }: StateContext<StripeStateModel>): void {
    patchState({ temporaryPaymentMethod: undefined });
  }

  @Action([SetNewBillingDetails])
  setNewBillingDetails({ patchState }: StateContext<StripeStateModel>, { newBillingDetails }: SetNewBillingDetails): void {
    patchState({ newBillingDetails });
  }

  @Action([SetProvidedBillingEmail])
  setProvidedBillingEmail({ patchState }: StateContext<StripeStateModel>, { providedBillingEmail }: SetProvidedBillingEmail): void {
    patchState({ providedBillingEmail });
  }

  @Action([RemoveNewBillingDetails])
  removeNewBillingDetails({ patchState }: StateContext<StripeStateModel>): void {
    patchState({ newBillingDetails: undefined, providedBillingEmail: undefined });
  }

  @Action([UpdatePaymentMethodBillingDetails])
  updatePaymentMethodBillingDetails(
    _: StateContext<StripeStateModel>,
    { paymentMethodId, newBillingDetails }: UpdatePaymentMethodBillingDetails,
  ): Observable<string> {
    const stripeUserId = this.store.selectSnapshot(StripeState.getUserId);
    return this.stripeApiService.updatePaymentMethod2(stripeUserId, paymentMethodId, newBillingDetails).pipe(
      take(1),
    );
  }

  @Action([SavePaymentMethod])
  savePaymentMethod(
    _: StateContext<StripeStateModel>,
    { paymentMethodId, setupIntentId }: SavePaymentMethod,
  ): Observable<string> {
    const stripeUserId = this.store.selectSnapshot(StripeState.getUserId);
    return this.paymentMethodApiService.savePaymentMethod(stripeUserId, paymentMethodId, setupIntentId).pipe(
      take(1),
    );
  }

  @Action([SetDefaultPaymentMethod])
  setDefaultPaymentMethod(
    { patchState }: StateContext<StripeStateModel>,
    { paymentMethodId }: SetDefaultPaymentMethod,
  ): Observable<unknown> {
    return this.authService.getUser().pipe(
      switchMap(user => {
        let contactId: number = null;
        let staffId: number = null;

        if (user instanceof ContactUser) {
          contactId = user.getContact().id;
        } else if (user instanceof AdminUser) {
          staffId = user.getStaffId();
        }

        return this.contactApiService.setDefaultPaymentMethodId(
          paymentMethodId,
          contactId,
          staffId,
        );
      }),
      tap(_ => {
        const defaultPaymentMethodIdLoadable = this.store.selectSnapshot(StripeState.getDefaultPaymentMethodIdLoadable);
        // sending nullish paymentMethodId to the request above causes a server error, so send an empty string but set undefined here
        patchState({ defaultPaymentMethodId: { ...defaultPaymentMethodIdLoadable, data: paymentMethodId || undefined } });
      }),
      take(1),
    );
  }

  @Action([DeletePaymentMethod])
  deletePaymentMethod(
    _: StateContext<StripeStateModel>,
    { paymentMethodId }: DeletePaymentMethod,
  ): Observable<unknown> {
    const stripeUserId = this.store.selectSnapshot(StripeState.getUserId);
    return this.paymentMethodApiService.detachPaymentMethodV5(stripeUserId, paymentMethodId).pipe(
      take(1),
    );
  }

  @Action([SetPaymentMethodMode])
  setPaymentMethodMode({ patchState }: StateContext<StripeStateModel>, { paymentMethodMode }: SetPaymentMethodMode): void {
    patchState({ paymentMethodMode });
  }

  @Action([SetNewPaymentMethodType])
  setNewPaymentMethodType({ patchState }: StateContext<StripeStateModel>, { newPaymentMethodType }: SetNewPaymentMethodType): void {
    patchState({ newPaymentMethodType });
  }

  @Action([ResetState])
  resetState({ setState }: StateContext<StripeStateModel>): void {
    setState(DEFAULT_STRIPE_STATE_MODEL);
  }
}
